/**
 * Popover components
 * Simple popover components using Tailwind CSS
 */

import React, { createContext, useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'

// Context for popover state
interface PopoverContextType {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLElement>
}

const PopoverContext = createContext<PopoverContextType | null>(null)

// Popover Root
interface PopoverProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

export const Popover: React.FC<PopoverProps> = ({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const triggerRef = useRef<HTMLElement>(null)

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(newOpen)
      }
      onOpenChange?.(newOpen)
    },
    [controlledOpen, onOpenChange]
  )

  const contextValue = useMemo(
    () => ({ open, setOpen: handleOpenChange, triggerRef }),
    [open, handleOpenChange]
  )

  return (
    <PopoverContext.Provider value={contextValue}>
      <div className="relative inline-block">{children}</div>
    </PopoverContext.Provider>
  )
}

// Popover Trigger
interface PopoverTriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean
  children: React.ReactNode
}

export const PopoverTrigger = React.forwardRef<HTMLElement, PopoverTriggerProps>(
  ({ asChild, children, onClick, ...props }, ref) => {
    const context = useContext(PopoverContext)
    if (!context) throw new Error('PopoverTrigger must be used within Popover')

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      context.setOpen(!context.open)
      if (onClick) {
        ;(onClick as React.MouseEventHandler<HTMLElement>)(e)
      }
    }

    // If asChild is true and children is a single React element, clone it with the click handler
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{
          onClick?: React.MouseEventHandler
          ref?: React.Ref<HTMLElement>
        }>,
        {
          onClick: handleClick,
          ref: ref || context.triggerRef,
        }
      )
    }

    return (
      <button type="button" onClick={handleClick} {...props}>
        {children}
      </button>
    )
  }
)
PopoverTrigger.displayName = 'PopoverTrigger'

// Popover Content
interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  children: React.ReactNode
}

export const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className, align = 'center', side = 'bottom', sideOffset = 4, children, ...props }, _ref) => {
    const context = useContext(PopoverContext)
    const contentRef = useRef<HTMLDivElement>(null)

    if (!context) throw new Error('PopoverContent must be used within Popover')

    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
        if (isBugsnapUiEvent(event.target, contentRef.current)) return
        if (
          contentRef.current &&
          !contentRef.current.contains(event.target as Node) &&
          !contentRef.current.parentElement?.contains(event.target as Node)
        ) {
          context.setOpen(false)
        }
      }

      // Close on escape key
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          context.setOpen(false)
        }
      }

      if (context.open) {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
          document.removeEventListener('keydown', handleEscape)
        }
      }
    }, [context.open, context])

    if (!context.open) return null

    // Position classes based on side
    const positionClasses = {
      top: 'bottom-full mb-2',
      bottom: 'top-full mt-2',
      left: 'right-full mr-2',
      right: 'left-full ml-2',
    }

    // Alignment classes
    const alignClasses = {
      start: side === 'top' || side === 'bottom' ? 'left-0' : 'top-0',
      center:
        side === 'top' || side === 'bottom'
          ? 'left-1/2 -translate-x-1/2'
          : 'top-1/2 -translate-y-1/2',
      end: side === 'top' || side === 'bottom' ? 'right-0' : 'bottom-0',
    }

    return (
      <div
        ref={contentRef}
        className={cn(
          'absolute z-50 min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white p-4 shadow-md',
          'dark:border-gray-700 dark:bg-gray-800',
          positionClasses[side],
          alignClasses[align],
          className
        )}
        style={{
          marginTop: side === 'bottom' ? sideOffset : undefined,
          marginBottom: side === 'top' ? sideOffset : undefined,
        }}
        {...props}
      >
        {children}
      </div>
    )
  }
)
PopoverContent.displayName = 'PopoverContent'
